import type { QueryClient } from '@tanstack/react-query';
import { api } from './api/client';
import { DECISION_KEY, PULSE_KEY, THREAD_KEY } from './api/keys';
import { fetchMe, ME_KEY } from './auth/useMe';

/** How long the first render waits for the boot data at most; the queries carry on in the cache either way. */
export const BOOT_WAIT_MS = 800;

/**
 * Starts the first screen's queries before React renders (planning-app.md §4.5): `/api/me` and the
 * room's data — index.html preloads the same URLs, so these resolve from the preload cache and the
 * first render paints the page in one pass instead of shell → loading → content.
 */
export function prefetchBoot(qc: QueryClient, pathname: string): Promise<unknown> {
  const jobs: Promise<unknown>[] = [qc.prefetchQuery({ queryKey: ME_KEY, queryFn: fetchMe, staleTime: 60_000 })];
  const decision = /^\/decisions\/([^/]+)$/.exec(pathname);
  const thread = /^\/threads\/([^/]+)$/.exec(pathname);
  if (pathname === '/') jobs.push(qc.prefetchQuery({ queryKey: PULSE_KEY, queryFn: api.pulse }));
  else if (decision) {
    const id = decodeURIComponent(decision[1]!);
    jobs.push(qc.prefetchQuery({ queryKey: DECISION_KEY(id), queryFn: () => api.decision(id), retry: false }));
  } else if (thread) {
    const id = decodeURIComponent(thread[1]!);
    jobs.push(qc.prefetchQuery({ queryKey: THREAD_KEY(id), queryFn: () => api.thread(id), retry: false }));
  }
  return Promise.allSettled(jobs);
}

export function bootDeadline(ms = BOOT_WAIT_MS): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
